import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Logo } from 'components/Logo';
import { Button, Input } from 'components/ui';
import CirculerShadow from 'components/ui/CirculerShadow';
import { BACK_TO_WEBSITE_LINK } from 'config/constants';

export const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword, signOut, user } = useAuth();
  const location = useLocation();

  // Sign out if coming from Settings and still authenticated
  useEffect(() => {
    const fromSettings = location.state?.fromSettings;
    if (fromSettings && user) {
      signOut();
    }
  }, [location.state, user, signOut]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await resetPassword(email);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center bg-gray-100">
      <div
        className="w-full justify-between fixed top-0 max-w-7xl mx-auto z-10 flex pt-6 px-6"
      >
        <Logo />
        <a
          href={BACK_TO_WEBSITE_LINK}
          className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
        >
          ← Back to Website
        </a>
      </div>

      <div className="my-4 py-6 text-center">
        <h1 className="text-4xl font-medium">Reset your password</h1>
        <p className="text-xs">Enter your email to receive a password reset link</p>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        {!success ? (
          <form className="space-y-3" onSubmit={handleSubmit}>
            <Input
              type="email"
              label="Email"
              name="email"
              placeholder="Email"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              required
              disabled={loading}
            />

            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>
            )}

            <Button type="submit" className="mt-6" variant="primary" size="sm" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-100 text-green-700 rounded-md text-sm">
              Check your email for a password reset link. The link will expire in 1 hour.
            </div>
          </div>
        )}

        <p className="mt-4 text-center text-xs text-gray-600">
          Remember your password?{' '}
          <Link to="/login" className="text-link font-medium underline">
            Back to Login
          </Link>
        </p>
      </div>

      <div className="md:absolute hidden -top-30 right-0 ">
        <CirculerShadow className="" />
      </div>
    </div>
  );
};
